/**
 * Username helpers for the playground profile-provisioning flow.
 *
 * The Cloud Run `/v1/profile/create` route only auto-provisions a valid,
 * guaranteed-unique `anonymous_<userId>` handle when NO `username` is sent.
 * The moment a `username` is present it must pass a strict charset
 * (`/^[A-Za-z0-9_-]+$/`), length (3-38), uniqueness, profanity, and
 * reserved-word gate. Client-derived display strings ("Pany Haritatos",
 * "Dev User _player2") fail that gate, so the auto-provision path must send a
 * body that structurally cannot carry a username.
 */

/** The server's URL-safe charset for usernames. */
const VALID_CHARSET = /^[A-Za-z0-9_-]+$/
/** The server's minimum username length. */
const MIN_LENGTH = 3
/** The server's maximum username length (mirrors `USERNAME_MAX_LENGTH`). */
const MAX_LENGTH = 38

/**
 * Coerce an arbitrary display string into a value the server's username gate
 * will accept for CHARSET and length. Never returns an empty/too-short value.
 *
 * - collapses any run of out-of-charset characters into a single `_`
 * - trims leading/trailing `_`/`-`
 * - truncates to the server maximum, re-trimming any trailing `_`/`-` the cut
 *   leaves behind
 * - falls back to a stable `player_<hex>` derived from the input when the
 *   sanitized result is shorter than the server minimum
 *
 * The return value is total over any input: it always matches
 * `/^[A-Za-z0-9_-]{3,38}$/`.
 *
 * This is a defense-in-depth guard for any explicit, caller-chosen username.
 * It does NOT re-introduce sending a derived handle on the auto-provision path.
 */
export function toValidUsername(raw: string): string {
  const collapsed = (raw ?? '')
    .replace(/[^A-Za-z0-9_-]+/g, '_')
    .replace(/^[_-]+|[_-]+$/g, '')
    .slice(0, MAX_LENGTH)
    .replace(/[_-]+$/g, '')

  if (collapsed.length >= MIN_LENGTH && VALID_CHARSET.test(collapsed)) {
    return collapsed
  }

  return `player_${stableHex(raw ?? '')}`
}

/**
 * Deterministic 8-char lowercase-hex derived from the input, so a given seed
 * always maps to the same fallback handle. Uses a simple FNV-1a hash — no
 * cryptographic guarantee is needed, only stability and charset-safety.
 */
function stableHex(seed: string): string {
  let hash = 0x811c9dc5
  for (let i = 0; i < seed.length; i++) {
    hash ^= seed.charCodeAt(i)
    hash = Math.imul(hash, 0x01000193)
  }
  return (hash >>> 0).toString(16).padStart(8, '0')
}

/**
 * The exact body accepted by `POST /v1/profile/create`. The controller reads
 * `username`, `description`, `birthdate`, and `onboardingCompleted`; `name`
 * and `isAnonymous` are ignored by the server today.
 *
 * `username` is intentionally OPTIONAL and, on the auto-provision path, absent:
 * the two builders below make it impossible to auto-provision with a
 * client-minted username.
 */
export interface CreateProfileBody {
  description: string
  birthdate: string
  onboardingCompleted: boolean
  username?: string
}

/**
 * Build the auto-provision body. This overload cannot carry a `username`:
 * omitting it routes the server into its `anonymous_<userId>` path, which is
 * always valid and unique. This is the primary root fix.
 */
export function buildAutoProvisionProfileBody(fields: {
  description: string
  birthdate: string
  onboardingCompleted: boolean
}): CreateProfileBody {
  return {
    description: fields.description,
    birthdate: fields.birthdate,
    onboardingCompleted: fields.onboardingCompleted,
  }
}

/**
 * Build a create body for an EXPLICIT, caller-chosen username. The raw handle
 * is run through `toValidUsername` so it can never violate the server gate.
 */
export function buildExplicitProfileBody(fields: {
  username: string
  description: string
  birthdate: string
  onboardingCompleted: boolean
}): CreateProfileBody {
  return {
    username: toValidUsername(fields.username),
    description: fields.description,
    birthdate: fields.birthdate,
    onboardingCompleted: fields.onboardingCompleted,
  }
}
